// Notifications at /o/:slug/notifications (F7 / FR-N7): the DS NotificationsScreen — title +
// "N unread", "Mark all read" at the right, then one white card of hairline rows. The same
// feed as the bell (useNotifications: GET /me/notifications plus live WS pushes), but all of it:
// every page loads at once, and "Load more" shows only to retry a page that failed. Clicking a
// row marks it read and opens its link, as the bell does.
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button } from "@grackle/ui";
import { AppShell } from "../components/AppShell";
import { EmptyState, PageLoader } from "../components/ui";
import { useSession } from "../session";
import { NotificationRow } from "./NotificationRow";
import { notificationPath } from "./display";
import { useNotifications } from "./useNotifications";
import "./notifications.css";

export default function NotificationsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);
  const { items, unread, nextCursor, loadingMore, loadMore, markRead, markAllRead } = useNotifications(
    (path) => navigate(path),
    { all: true },
  );

  if (sessionLoading) return <PageLoader label="Loading notifications…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  const canRecord = user.email_verified;

  return (
    <AppShell org={org} user={user} section="notifications" canRecord={canRecord}>
      <div className="lib-header">
        <div className="lib-header-copy">
          <h1 className="lib-header-title">Notifications</h1>
          <div className="lib-header-sub">{`${unread} unread`}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={markAllRead} disabled={unread === 0}>
          Mark all read
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          className="lib-empty"
          icon="bell"
          title="You're all caught up"
          body="Recordings shared with you, ready transcripts and meeting notes show up here."
        />
      ) : (
        <>
          <div className="notif-list notif-list--page" role="list">
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                role="listitem"
                className={`notif-item${n.read_at ? "" : " is-unread"}`}
                disabled={n.recording?.state === "deleted"}
                onClick={() => {
                  if (!n.read_at) markRead(n.id);
                  const path = notificationPath(n);
                  if (path) navigate(path);
                }}
              >
                <NotificationRow notification={n} />
              </button>
            ))}
          </div>
          {nextCursor ? (
            <div className="lib-load-more">
              <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </AppShell>
  );
}
